import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatMinutes(mins) {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function ZPanelPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show404, setShow404] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      setShow404(true);
      return;
    }
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/z-panel/stats`, { withCredentials: true });
      setStats(res.data);
    } catch {
      setShow404(true);
    } finally {
      setLoading(false);
    }
  };

  if (show404) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-[#1D1D1F]">404</h1>
          <p className="text-[#86868B] mt-2">Page not found</p>
          <button onClick={() => navigate('/')} className="text-[#007AFF] mt-4 text-sm hover:underline">Go home</button>
        </div>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { users, logins, deals, time_spent } = stats || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 sm:p-8" data-testid="z-panel">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-2xl font-bold text-white/90 tracking-tight">z-panel</h1>
            <p className="text-white/30 text-sm mt-1">Aggregated metrics only — no user data exposed</p>
          </div>
          <button onClick={() => navigate('/')} className="text-white/30 hover:text-white/60 text-sm transition-colors">
            exit
          </button>
        </div>

        {/* Section: Signups */}
        <Section title="Signups">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Total Users" value={users?.total} />
            <Metric label="Today" value={users?.today} accent />
            <Metric label="Last 7 Days" value={users?.last_7d} />
            <Metric label="Last 30 Days" value={users?.last_30d} />
          </div>
          {/* Mini signup trend */}
          {users?.signup_trend && (
            <div className="mt-4 flex items-end gap-1 h-16">
              {users.signup_trend.map((d, i) => {
                const max = Math.max(...users.signup_trend.map(x => x.count), 1);
                const height = Math.max((d.count / max) * 100, 4);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                    <div
                      className={`w-full rounded-sm transition-all ${d.count > 0 ? 'bg-emerald-500' : 'bg-white/10'}`}
                      style={{ height: `${height}%`, minHeight: '2px' }}
                    />
                    {i % 2 === 0 && (
                      <span className="text-[9px] text-white/20 hidden sm:block">{d.date.split(' ')[1]}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Section: Logins */}
        <Section title="Login Activity">
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Total Logins" value={logins?.total} />
            <Metric label="Active (24h)" value={logins?.active_24h} accent />
            <Metric label="Active (7d)" value={logins?.active_7d} />
          </div>
        </Section>

        {/* Section: Deals */}
        <Section title="Deals Entered">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Total Deals" value={deals?.total} />
            <Metric label="Today" value={deals?.today} accent />
            <Metric label="Last 7 Days" value={deals?.last_7d} />
            <Metric label="Last 30 Days" value={deals?.last_30d} />
          </div>
        </Section>

        {/* Section: Time Spent */}
        <Section title="Time on App">
          <div className="grid grid-cols-3 gap-3">
            <Metric label="All Time" value={formatMinutes(time_spent?.total_minutes || 0)} raw />
            <Metric label="Today" value={formatMinutes(time_spent?.today_minutes || 0)} raw accent />
            <Metric label="Last 7 Days" value={formatMinutes(time_spent?.last_7d_minutes || 0)} raw />
          </div>
        </Section>

        <p className="text-white/10 text-xs text-center mt-10">internal use only — no personal data stored here</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-xs font-semibold text-white/40 uppercase tracking-widest mb-3">{title}</h2>
      <div className="bg-white/5 rounded-lg border border-white/10 p-4">
        {children}
      </div>
    </div>
  );
}

function Metric({ label, value, accent, raw }) {
  return (
    <div className="text-center sm:text-left">
      <p className="text-white/35 text-xs uppercase tracking-wider">{label}</p>
      <p className={`text-2xl sm:text-3xl font-bold mt-1 ${accent ? 'text-emerald-400' : 'text-white/90'}`}>
        {raw ? value : (value ?? 0)}
      </p>
    </div>
  );
}
